import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import BrownianMotion from './BrownianMotion';
import { useIsMobile } from '@/hooks/use-mobile';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
const HeroSection = () => {
  const isMobile = useIsMobile();
  const [refreshVersion] = useState(Date.now());

  const headlineRef = useRef<HTMLSpanElement | null>(null);
  const subheadlineRef = useRef<HTMLSpanElement | null>(null);
  const [headlineFontPx, setHeadlineFontPx] = useState<number>(48);
  const [subheadlineFontPx, setSubheadlineFontPx] = useState<number>(20);
  const [paragraphFontPx, setParagraphFontPx] = useState<number>(14);

  const headlineText = useMemo(() => "a 'niche-alpha' quant hedge fund", []);

  useEffect(() => {
    console.log('HeroSection loaded - Mobile:', isMobile);
  }, [isMobile]);

  useLayoutEffect(() => {
    const headlineEl = headlineRef.current;
    const subheadlineEl = subheadlineRef.current;

    const compute = () => {
      // 5% margin on each side => usable width = 90vw, with additional safety margin
      const screenWidth = window.innerWidth;
      const availableWidth = Math.floor(screenWidth * 0.88);

      // Binary search helper
      const findBestFontSize = (el: HTMLSpanElement, min: number, max: number): number => {
        let lo = min;
        let hi = max;
        let best = min;

        const prevWhiteSpace = el.style.whiteSpace;
        const prevFontSize = el.style.fontSize;
        const prevVisibility = el.style.visibility;

        el.style.whiteSpace = 'nowrap';
        el.style.visibility = 'hidden';

        while (lo <= hi) {
          const mid = Math.floor((lo + hi) / 2);
          el.style.fontSize = `${mid}px`;

          const fits = el.scrollWidth <= availableWidth;
          if (fits) {
            best = mid;
            lo = mid + 1;
          } else {
            hi = mid - 1;
          }
        }

        el.style.whiteSpace = prevWhiteSpace;
        el.style.fontSize = prevFontSize;
        el.style.visibility = prevVisibility;

        return best;
      };

      // Adjust max font sizes based on screen width for better mobile scaling
      const headlineMax = screenWidth < 480 ? 36 : screenWidth < 768 ? 48 : 64;
      const subheadlineMax = screenWidth < 480 ? 14 : screenWidth < 768 ? 18 : 24;

      if (headlineEl) {
        const headlineSize = findBestFontSize(headlineEl, 14, headlineMax);
        setHeadlineFontPx(headlineSize);
      }

      if (subheadlineEl) {
        const subSize = findBestFontSize(subheadlineEl, 8, subheadlineMax);
        setSubheadlineFontPx(subSize);
        // Paragraph scales proportionally - roughly 70% of subheadline size
        setParagraphFontPx(Math.max(10, Math.round(subSize * 0.7)));
      }
    };

    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, [headlineText]);
  return <section className="relative min-h-screen flex items-center overflow-hidden grid-bg" key={`hero-${refreshVersion}`}>
      <div className="absolute inset-0 z-0">
        <div className="absolute top-1/2 left-1/2 w-96 h-96 -translate-x-1/2 -translate-y-1/2 rounded-full bg-delphi-red/5 blur-3xl"></div>
        <div className="absolute top-1/3 right-1/4 w-72 h-72 rounded-full bg-blue-500/5 blur-3xl"></div>
        <BrownianMotion particleCount={isMobile ? 3 : 5} speed={0.4} trailLength={50} />
      </div>

      <div className="container mx-auto px-4 py-8 xs:py-12 sm:py-16 md:py-24 lg:py-32 relative z-10">
        <div className="w-full">
          {/* Header at the top */}
          <div className="w-full flex flex-col items-end px-[5vw] text-right relative">
            <div className="absolute inset-0 bg-background/40 backdrop-blur-sm rounded-2xl -m-4 p-4"></div>
            <h1 className="hero-heading tracking-wide lowercase mb-4 xs:mb-6 leading-tight relative z-10 flex flex-col items-end">
              <span
              ref={headlineRef}
              className="whitespace-nowrap text-foreground"
              style={{ fontSize: `${headlineFontPx}px` }}>

                a <span className="text-delphi-red not-italic">​research focused </span> quant hedge fund
              </span>
              <span
              ref={subheadlineRef}
              className="text-delphi-red whitespace-nowrap"
              style={{ fontSize: `${subheadlineFontPx}px`, paddingTop: '3px', paddingBottom: '3px' }}>

                a unique approach to <span className="text-white">small scale</span>,<span className="text-white"> high-yield</span> derivatives trading
              </span>
            </h1>
            {/* Mobile-only paragraph aligned exactly under the header */}
            <p
            className="block md:hidden text-delphi-light/70 text-balance leading-relaxed px-2 xs:px-4 text-right relative z-10"
            style={{ fontSize: `${paragraphFontPx}px` }}>

              we apply cutting-edge hypotheses to financial markets, delivering exceptional returns while funding pure-theory scientific research.
            </p>
          </div>

          {/* Under header: left = buttons, right = paragraph */}
          <div className="mt-6 xs:mt-8 sm:mt-12 md:mt-14 md:grid md:grid-cols-12 md:gap-8 lg:gap-12 items-start">
            {/* Left column: actions */}
            <div className="md:col-span-5 lg:col-span-5 order-1">
              <div className="mb-5 sm:mb-8 md:mb-10">
                <div className="flex flex-col xs:flex-row md:flex-col items-stretch md:items-start justify-start gap-3 xs:gap-4">
                  {/* Primary button with graphic-style background */}
                  <div className="relative w-full xs:flex-1 rounded-md bg-gradient-to-br from-delphi-red/30 via-delphi-red/10 to-transparent">
                    <Link to="/performance" className="relative block w-full rounded-md bg-delphi-gray/20 backdrop-blur-md border border-delphi-gray/30 text-delphi-light font-medium flex items-center justify-center gap-2 whitespace-nowrap px-4 xs:px-5 py-2.5 sm:px-6 sm:py-3 hover-scale">
                      <div className="absolute inset-0 opacity-5">
                        <div className="w-full h-full bg-grid-pattern"></div>
                      </div>
                      <span className="relative z-10">View Performance</span>
                      <ArrowRight size={16} className="relative z-10" />
                    </Link>
                  </div>

                  {/* Secondary button with the same graphic style */}
                  <div className="relative w-full xs:flex-1 rounded-md bg-gradient-to-br from-delphi-red/30 via-delphi-red/10 to-transparent">
                    <Link to="/contact" className="relative block w-full rounded-md bg-delphi-gray/20 backdrop-blur-md border border-delphi-gray/30 text-delphi-light font-medium flex items-center justify-center gap-2 whitespace-nowrap px-4 xs:px-5 py-2.5 sm:px-6 sm:py-3 hover-scale">
                      <div className="absolute inset-0 opacity-5">
                        <div className="w-full h-full bg-grid-pattern"></div>
                      </div>
                      <span className="relative z-10">See for Yourself</span>
                      <ArrowRight size={16} className="relative z-10" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Right column: paragraph */}
            <div className="hidden md:block md:col-span-7 lg:col-span-7 order-2 mt-6 md:mt-0 pr-[5vw]">
              <p className="text-sm xs:text-base sm:text-xl text-delphi-light/70 max-w-prose ml-auto text-balance leading-relaxed md:text-2xl text-right">
                we apply cutting-edge hypotheses to financial markets, delivering exceptional returns while funding pure-theory scientific research.
              </p>
            </div>
          </div>

          {/* Full-width stats row under buttons + paragraph */}
          <div className="mt-8 xs:mt-10 sm:mt-12 md:mt-14">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 xs:gap-4 sm:gap-6">
              <div className="relative rounded-xl bg-gradient-to-br from-delphi-red/30 via-delphi-red/10 to-transparent hover-scale transition-transform duration-300">
                <div className="absolute inset-0 rounded-xl bg-delphi-gray/20 backdrop-blur-md border border-delphi-gray/30"></div>
                <div className="absolute inset-0 rounded-xl opacity-5">
                  <div className="w-full h-full bg-grid-pattern"></div>
                </div>
                <div className="relative z-10 text-center p-3 xs:p-4 sm:p-5">
                  <p className="text-foreground text-lg xs:text-xl sm:text-3xl md:text-4xl font-bold mb-1 sm:mb-2">+130%</p>
                  <p className="text-[10px] xs:text-xs sm:text-sm text-delphi-light/70">Annual Return After Fees</p>
                </div>
              </div>

              <div className="relative rounded-xl bg-gradient-to-br from-delphi-red/30 via-delphi-red/10 to-transparent hover-scale transition-transform duration-300">
                <div className="absolute inset-0 rounded-xl bg-delphi-gray/20 backdrop-blur-md border border-delphi-gray/30"></div>
                <div className="absolute inset-0 rounded-xl opacity-5">
                  <div className="w-full h-full bg-grid-pattern"></div>
                </div>
                <div className="relative z-10 text-center p-3 xs:p-4 sm:p-5">
                  <p className="text-foreground text-lg xs:text-xl sm:text-3xl md:text-4xl font-bold mb-1 sm:mb-2">1.21</p>
                  <p className="text-[10px] xs:text-xs sm:text-sm text-delphi-light/70">Sharpe Ratio</p>
                </div>
              </div>

              <div className="relative rounded-xl bg-gradient-to-br from-delphi-red/30 via-delphi-red/10 to-transparent hover-scale transition-transform duration-300">
                <div className="absolute inset-0 rounded-xl bg-delphi-gray/20 backdrop-blur-md border border-delphi-gray/30"></div>
                <div className="absolute inset-0 rounded-xl opacity-5">
                  <div className="w-full h-full bg-grid-pattern"></div>
                </div>
                <div className="relative z-10 text-center p-3 xs:p-4 sm:p-5">
                  <p className="text-foreground text-lg xs:text-xl sm:text-3xl md:text-4xl font-bold mb-1 sm:mb-2">$10M</p>
                  <p className="text-[10px] xs:text-xs sm:text-sm text-delphi-light/70">Target AUM Capacity</p>
                </div>
              </div>

              <div className="relative rounded-xl bg-gradient-to-br from-delphi-red/30 via-delphi-red/10 to-transparent hover-scale transition-transform duration-300">
                <div className="absolute inset-0 rounded-xl bg-delphi-gray/20 backdrop-blur-md border border-delphi-gray/30"></div>
                <div className="absolute inset-0 rounded-xl opacity-5">
                  <div className="w-full h-full bg-grid-pattern"></div>
                </div>
                <div className="relative z-10 text-center p-3 xs:p-4 sm:p-5">
                  <p className="text-foreground text-lg xs:text-xl sm:text-3xl md:text-4xl font-bold mb-1 sm:mb-2">0%</p>
                  <p className="text-[10px] xs:text-xs sm:text-sm text-delphi-light/70">Management Fee</p>
                  <p className="text-[8px] xs:text-[10px] text-delphi-light/60 mt-1">We Only Profit When Partners Do</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
export default HeroSection;